import getCharactersTypedSinceRequest from './getCharactersTypedSinceRequest';
import {
    removeCurrentlyShownSuggestionFromEditor,
    setCurrentlyShownSuggestion,
    addRecentlyRejectedSuggestionsTelemetry,
} from '../actions/smartComposePluginActions';
import Suggestion from '../store/schema/Suggestion';
import { logSuggestionAccepted } from '../telemetryHelpers/logDatapoints';
import updateTelemetryForSuggestionOnSuggestionAccepted from '../telemetryHelpers/updateTelemetryForSuggestionOnSuggestionAccepted';
import updateTelemetryForSuggestionOnSuggestionRejected from '../telemetryHelpers/updateTelemetryForSuggestionOnSuggestionRejected';
import showSuggestionInEditor from '../utils/showSuggestionInEditor';
import { IEditor } from 'roosterjs-editor-types';
import { ISmartComposeSession } from '@qas/smartcompose-tslibrary/dist/src/session';
import {
    RejectionReason,
    AcceptanceReason,
} from '@qas/smartcompose-tslibrary/dist/src/smartComposeInteraction';

export default function showSuggestion(
    editor: IEditor,
    charactersBeforeCursorAtTimeOfCurrentRequest: string,
    newSuggestion: Suggestion,
    charactersBeforeCursor: string,
    charactersTypedInSession: number,
    currentSmartComposeSession: ISmartComposeSession,
    isDarkMode: boolean
) {
    if (newSuggestion) {
        let suggestionShown: boolean = false;
        if (charactersBeforeCursorAtTimeOfCurrentRequest == charactersBeforeCursor) {
            // in this case, the state before the request and the current state agree, so just proceed to show the suggestion
            showSuggestionInternal(
                editor,
                newSuggestion,
                charactersTypedInSession,
                currentSmartComposeSession,
                isDarkMode,
                newSuggestion.suggestionText?.length /* numberOfCharactersRendered */
            );
            suggestionShown = true;
        } else {
            // in this case the characters before the request and the current state don't agree. We need to check to see if any new characters typed since the request match the suggestion and then trim the suggestion
            const charactersTypedSinceRequest = getCharactersTypedSinceRequest(
                charactersBeforeCursorAtTimeOfCurrentRequest,
                charactersBeforeCursor
            );
            if (
                charactersTypedSinceRequest.length > 0 &&
                doesSuggestionStartWith(newSuggestion.suggestionText, charactersTypedSinceRequest)
            ) {
                const originalSuggestionLength = newSuggestion.suggestionText.length;
                newSuggestion.suggestionText = trimPrefixFromSuggestionText(
                    newSuggestion.suggestionText,
                    charactersTypedSinceRequest
                );

                if (newSuggestion.suggestionText.length === 0) {
                    newSuggestion.suggestionTelemetry.numberOfMatchingCharactersTyped = originalSuggestionLength;
                    updateTelemetryForSuggestionOnSuggestionAccepted(
                        newSuggestion.suggestionTelemetry,
                        '',
                        AcceptanceReason.TypedEverything
                    );
                    logSuggestionAccepted(
                        newSuggestion.suggestionTelemetry,
                        currentSmartComposeSession,
                        0
                    );
                } else {
                    showSuggestionInternal(
                        editor,
                        newSuggestion,
                        charactersTypedInSession,
                        currentSmartComposeSession,
                        isDarkMode,
                        originalSuggestionLength - charactersTypedSinceRequest.length
                    );
                    suggestionShown = true;
                }
            }
        }

        if (!suggestionShown) {
            updateTelemetryForSuggestionOnSuggestionRejected(
                newSuggestion.suggestionTelemetry,
                RejectionReason.NotSeen
            );
            addRecentlyRejectedSuggestionsTelemetry(newSuggestion.suggestionTelemetry);
        }
    } else {
        removeCurrentlyShownSuggestionFromEditor(editor);
    }
}

function showSuggestionInternal(
    editor: IEditor,
    newSuggestion: Suggestion,
    charactersTypedInSession: number,
    currentSmartComposeSession: ISmartComposeSession,
    isDarkMode: boolean,
    numberOfCharactersRendered: number
) {
    removeCurrentlyShownSuggestionFromEditor(editor);
    setCurrentlyShownSuggestion(newSuggestion);
    newSuggestion.suggestionNode = showSuggestionInEditor(
        editor,
        newSuggestion.suggestionText,
        newSuggestion.suggestionTelemetry,
        charactersTypedInSession,
        currentSmartComposeSession,
        isDarkMode,
        numberOfCharactersRendered
    );
}

function doesSuggestionStartWith(suggestionText: string, prefix: string): boolean {
    if (prefix?.length <= suggestionText?.length) {
        return suggestionText.indexOf(prefix) === 0;
    } else {
        return prefix.indexOf(suggestionText) === 0;
    }
}

function trimPrefixFromSuggestionText(suggestionText: string, prefix: string): string {
    return suggestionText.substring(prefix.length);
}
